import { mergeLiveTranscriptSnapshot, isInjectedSayPrompt } from '../lib/live-transcript-reconciler.js'
import type { LiveSessionEvent } from './liveTypes.js'

export type LiveTranscriptRole = 'user' | 'model'

export interface LiveTranscriptEntry {
  id: string
  role: LiveTranscriptRole
  text: string
  isFinal: boolean
  turnId?: string
  source?: 'outputTranscript' | 'text'
  timestamp: Date
  updatedAt: number
}

export interface LiveTranscriptState {
  entries: LiveTranscriptEntry[]
}

export function createLiveTranscriptState(
  entries: LiveTranscriptEntry[] = []
): LiveTranscriptState {
  return { entries: [...entries] }
}

export function reduceLiveTranscriptEvent(
  state: LiveTranscriptState,
  event: LiveSessionEvent,
  now = Date.now()
): LiveTranscriptState {
  switch (event.type) {
    case 'inputTranscript':
      if (isInjectedSayPrompt(event.text)) return state
      return upsertTranscriptEntry(state, 'user', event.text, event.isFinal, now, event.turnId)

    case 'outputTranscript':
      return upsertTranscriptEntry(
        state,
        'model',
        event.text,
        event.isFinal,
        now,
        event.turnId,
        'outputTranscript'
      )

    case 'text':
      return appendModelText(state, event.text, now, event.turnId)

    case 'turnComplete':
    case 'generationComplete':
      return finalizeDrafts(state, 'model', now)

    case 'interrupted':
      return clearDrafts(state, 'model')

    case 'disconnected':
      return finalizeDrafts(state, undefined, now)

    default:
      return state
  }
}

export function resetLiveTranscriptState(): LiveTranscriptState {
  return createLiveTranscriptState()
}

export function getLatestTranscriptEntry(
  state: LiveTranscriptState,
  role?: LiveTranscriptRole
): LiveTranscriptEntry | null {
  for (let index = state.entries.length - 1; index >= 0; index -= 1) {
    const entry = state.entries[index]
    if (entry && (!role || entry.role === role)) {
      return entry
    }
  }
  return null
}

export function liveTranscriptText(state: LiveTranscriptState): string {
  return state.entries
    .filter(entry => entry.text.trim().length > 0)
    .map(entry => `${entry.role === 'user' ? 'User' : 'Assistant'}: ${entry.text}`)
    .join('\n')
}

export function isTranscriptEvent(event: LiveSessionEvent): boolean {
  return (
    event.type === 'inputTranscript' ||
    event.type === 'outputTranscript' ||
    event.type === 'text' ||
    event.type === 'turnComplete' ||
    event.type === 'generationComplete'
  )
}

function upsertTranscriptEntry(
  state: LiveTranscriptState,
  role: LiveTranscriptRole,
  text: string,
  isFinal: boolean,
  now: number,
  turnId?: string,
  source?: 'outputTranscript' | 'text'
): LiveTranscriptState {
  const normalizedText = normalizeTranscriptText(text)
  if (!normalizedText) return state

  const turnIndex = findTurnIndex(state.entries, role, turnId)
  const draftIndex = turnIndex >= 0 ? turnIndex : findDraftIndex(state.entries, role)
  if (source === 'outputTranscript' && turnIndex >= 0) {
    const existing = state.entries[turnIndex]
    if (existing?.source === 'outputTranscript' && existing.text === normalizedText && existing.isFinal === isFinal) {
      return state
    }
  }

  const entries = [...state.entries]
  const nextEntry = createTranscriptEntry(
    role,
    normalizedText,
    isFinal,
    now,
    entries.length,
    turnId,
    source
  )

  if (draftIndex >= 0) {
    const draft = entries[draftIndex]
    if (!draft) return state

    entries[draftIndex] = {
      ...draft,
      text: mergeLiveTranscriptSnapshot(draft.text, normalizedText),
      isFinal,
      ...(turnId ? { turnId } : draft.turnId ? { turnId: draft.turnId } : {}),
      ...(source ? { source } : draft.source ? { source: draft.source } : {}),
      updatedAt: now,
    }
    return { entries }
  }

  entries.push(nextEntry)
  return { entries }
}

function appendModelText(
  state: LiveTranscriptState,
  text: string,
  now: number,
  turnId?: string
): LiveTranscriptState {
  const normalizedText = normalizeTranscriptText(text)
  if (!normalizedText) return state

  const turnIndex = findTurnIndex(state.entries, 'model', turnId)
  const existingTurn = turnIndex >= 0 ? state.entries[turnIndex] : undefined
  if (existingTurn?.source === 'outputTranscript') return state

  const draftIndex = turnIndex >= 0 ? turnIndex : findDraftIndex(state.entries, 'model')
  if (draftIndex < 0) {
    return upsertTranscriptEntry(state, 'model', normalizedText, false, now, turnId, 'text')
  }

  const entries = [...state.entries]
  const draft = entries[draftIndex]
  if (!draft) return state

  entries[draftIndex] = {
    ...draft,
    text: mergeLiveTranscriptSnapshot(draft.text, text),
    ...(turnId ? { turnId } : draft.turnId ? { turnId: draft.turnId } : {}),
    source: 'text',
    updatedAt: now,
  }
  return { entries }
}

function finalizeDrafts(
  state: LiveTranscriptState,
  role: LiveTranscriptRole | undefined,
  now: number
): LiveTranscriptState {
  let changed = false
  const entries = state.entries.map(entry => {
    if (entry.isFinal || (role && entry.role !== role)) {
      return entry
    }

    changed = true
    return {
      ...entry,
      isFinal: true,
      updatedAt: now,
    }
  })

  return changed ? { entries } : state
}

function clearDrafts(
  state: LiveTranscriptState,
  role: LiveTranscriptRole | undefined
): LiveTranscriptState {
  const entries = state.entries.filter(entry => entry.isFinal || (role && entry.role !== role))
  return entries.length === state.entries.length ? state : { entries }
}

function createTranscriptEntry(
  role: LiveTranscriptRole,
  text: string,
  isFinal: boolean,
  now: number,
  index: number,
  turnId?: string,
  source?: 'outputTranscript' | 'text'
): LiveTranscriptEntry {
  return {
    id: `live-${role}-${now}-${index}`,
    role,
    text,
    isFinal,
    ...(turnId ? { turnId } : {}),
    ...(source ? { source } : {}),
    timestamp: new Date(now),
    updatedAt: now,
  }
}

function findTurnIndex(
  entries: LiveTranscriptEntry[],
  role: LiveTranscriptRole,
  turnId?: string
): number {
  if (!turnId) return -1
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry?.role === role && entry.turnId === turnId) return index
  }
  return -1
}

function findDraftIndex(entries: LiveTranscriptEntry[], role: LiveTranscriptRole): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry?.role === role && !entry.isFinal) {
      return index
    }
  }
  return -1
}

function normalizeTranscriptText(text: string): string {
  return text.trim()
}
